const WEIGHT = { kg: 1, g: 0.001 };
const VOLUME = { l: 1, cl: 0.01, ml: 0.001, dl: 0.1 };
const PIECES = new Set(["u", "unit", "unite", "unité", "piece", "pièce", "pcs", "pc"]);

export function positive(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function unitBase(unit) {
  const key = String(unit || "").trim().toLowerCase();
  if (PIECES.has(key)) return "u";
  if (WEIGHT[key]) return "kg";
  if (VOLUME[key]) return "L";
  return null;
}

export function measuredQuantity(count, size, unit) {
  const a = positive(count), b = positive(size);
  const key = String(unit || "").toLowerCase();
  if (!a || !b || !unitBase(unit)) return null;
  return a * b * (WEIGHT[key] || VOLUME[key] || 1);
}

export function quantityForBase(line, base) {
  if (base === "kg" && positive(line.total_weight_billed_g)) return Number(line.total_weight_billed_g) / 1000;
  if (base === "L" && positive(line.total_volume_billed_ml)) return Number(line.total_volume_billed_ml) / 1000;
  if (unitBase(line.unit) === base && positive(line.quantity)) return measuredQuantity(1, line.quantity, line.unit);
  if (base === "kg") return positive(line.total_weight_billed_g || line.total_weight_g) / 1000 || null;
  if (base === "L") return positive(line.total_volume_billed_ml || line.total_volume_ml) / 1000 || null;
  if (base === "u") {
    // A quantity already invoiced in pieces must not be multiplied by the pack label.
    if (unitBase(line.unit) === "u") return positive(line.quantity);
    return positive(line.total_units || line.pieces_count);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Feb 2026 §« retrait heuristique >=10 » — Classifieur sémantique par nature
// du produit. Alignement strict avec /app/backend/semantics.py — toute
// évolution des listes doit être répercutée des deux côtés.
//
// La quantité facturée n'entre JAMAIS dans la décision. Aucun mot-clé n'est
// une exception par marque : on classe par catégorie de matière.
// ---------------------------------------------------------------------------
const VOLUME_KEYWORDS = [
  "spiritueux", "whisky", "vodka", "gin", "rhum", "tequila",
  "cognac", "armagnac", "calvados", "pastis", "ricard", "anisette",
  "aperitif", "liqueur", "digestif", "amaretto", "limoncello",
  "sirop", "grenadine", "jus de",
  "huile", "vinaigre",
  "creme liquide", "creme anglaise", "creme fleurette",
  "lait entier", "lait demi", "lait ecreme",
];
const WEIGHT_KEYWORDS = [
  "viande", "boeuf", "veau", "agneau", "porc", "poulet", "dinde", "canard",
  "poisson", "saumon", "thon", "cabillaud", "morue", "bar", "dorade",
  "jambon", "saucisson", "chorizo", "coppa", "pancetta", "lard", "bacon",
  "charcuterie",
  "fromage", "parmesan", "gruyere", "emmental", "comte", "reblochon",
  "roquefort", "brie", "camembert", "chevre",
  "raclette", "mimolette", "tomme",
  "tomate", "pomme de terre", "carotte", "oignon", "poireau", "salade",
  "concombre", "poivron", "aubergine", "courgette", "haricot",
  "pomme", "poire", "orange", "citron", "banane", "raisin", "fraise",
  "fruits", "legumes",
  "farine", "sucre", "sel", "riz", "pate", "semoule", "lentille",
  "chocolat", "cacao", "cafe",
  "beurre", "creme fraiche epaisse", "creme epaisse",
];
const PIECE_KEYWORDS = [
  "canette", "boite", "bocal", "bocaux", "sachet",
  "brique", "coquille", "barquette", "gobelet", "pot", "verrine",
  "serviette", "essuie tout", "papier", "sac poubelle", "sac plastique",
  "film", "aluminium", "gant", "couvercle", "emballage",
  "oeuf",
  // Feb 2026 §« Coca Slim Can » — alignement strict avec backend/semantics.py.
  // Cible les boissons portionnées quand l'OCR laisse "can" ou une étiquette
  // "boisson portionnée". PAS "cola" seul (fût/sirop de cola reste L).
  "slim can", "boisson portionnee", "boisson portionnée",
  "capsule",
];

function normalizeText(text) {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ");
}

function scoreKeywords(haystack, keywords) {
  if (!haystack) return 0;
  const words = new Set(haystack.split(/\s+/).filter(Boolean));
  let count = 0;
  for (const kw of keywords) {
    const needle = normalizeText(kw);
    if (!needle) continue;
    if (needle.includes(" ")) {
      if (haystack.includes(needle)) count += 1;
      continue;
    }
    // Tolère pluriels FR (-s, -es, -x) sans faire matcher oeuf ⊂ boeuf.
    if (words.has(needle)
        || words.has(needle + "s")
        || words.has(needle + "es")
        || words.has(needle + "x")) {
      count += 1;
    }
  }
  return count;
}

export function semanticBaseUnit(line) {
  if (!line || typeof line !== "object") return null;
  const haystack = normalizeText(
    [line.product_name, line.raw_text, line.brand, line.category]
      .filter(Boolean).join(" ")
  );
  if (!haystack.trim()) return null;
  const scores = {
    L:  scoreKeywords(haystack, VOLUME_KEYWORDS),
    kg: scoreKeywords(haystack, WEIGHT_KEYWORDS),
    u:  scoreKeywords(haystack, PIECE_KEYWORDS),
  };
  const entries = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  if (entries[0][1] === 0) return null;
  if (entries[0][1] === entries[1][1]) return null;
  return entries[0][0];
}

// ---------------------------------------------------------------------------
// reviewMeasurement — Résolution de la base d'unité (u/kg/L) pour l'écran
// de vérification et la ligne validée. Priorité STRICTE :
//   1. Choix humain (`reviewed_base_unit_source === "human"`) → trust.
//   2. `reviewed_base_unit` legacy (avant l'ajout du flag `source`) → trust
//      tant que le back-end n'a pas identifié un mismatch OCR (dans ce cas
//      la migration `_purge_stale_reviewed_base` l'aura déjà retiré).
//   3. Décision métier OCR cohérente (`price_reference.base_unit`).
//   4. `unit_price_basis` ou `unit` de la ligne (u/kg/g/L/cl/ml).
//   5. Indice sémantique (nom / raw_text / marque / catégorie).
//   6. "" — À vérifier, l'utilisateur doit trancher dans le dropdown.
// ---------------------------------------------------------------------------
export function reviewMeasurement(line) {
  const reviewed = line.reviewed_base_unit != null && line.reviewed_base_unit !== "";
  const reference = line.price_reference;
  const base = reviewed
    ? line.reviewed_base_unit
    : (reference?.base_unit
        || unitBase(line.unit_price_basis)
        || unitBase(line.unit)
        || semanticBaseUnit(line)
        || "");
  const total = positive(line.line_total_ht);
  let quantity = reviewed ? positive(line.reviewed_quantity) : quantityForBase(line, base);
  // Only derive a missing quantity; do not undo invoice rounding in a known weight.
  if (!reviewed && !quantity && reference?.base_unit === base && positive(reference.price_per_base) && total) {
    quantity = total / reference.price_per_base;
    if (base === "u" && Math.abs(quantity - Math.round(quantity)) < 0.01) quantity = Math.round(quantity);
  }
  const sourceUnit = line.unit_price_basis || line.unit;
  const factor = measuredQuantity(1, 1, sourceUnit);
  const sourcePrice = unitBase(sourceUnit) === base && factor && positive(line.unit_price_ht)
    ? Number(line.unit_price_ht) / factor : null;
  const candidatePrice = reviewed ? positive(line.reviewed_price_ht) : positive(reference?.price_per_base) || sourcePrice;
  const consistentPrice = candidatePrice && quantity && total && Math.abs(candidatePrice * quantity - total) <= 0.011;
  const valid = Boolean(quantity && total && ["u", "kg", "L"].includes(base)
    && (!reviewed || !candidatePrice || consistentPrice)
    && (base !== "u" || Number.isInteger(quantity)));
  return { base, quantity, total, price: valid ? consistentPrice ? candidatePrice : total / quantity : null, valid };
}

// ---------------------------------------------------------------------------
// reviewPatch — Construit le patch appliqué à la ligne lorsque l'utilisateur
// édite un champ du formulaire de vérification.
//
// Feb 2026 §« retrait heuristique >=10 » — Auparavant ce helper tamponnait
// systématiquement `reviewed_base_unit`/`reviewed_quantity` sur CHAQUE
// édition (même non liée à la base : `line_total_ht`, `content_value`…),
// promouvant l'heuristique du moment au rang de « choix humain ». On
// distingue désormais deux cas :
//
//   • L'édition touche EXPLICITEMENT `reviewed_base_unit` ou
//     `reviewed_quantity`/`reviewed_price_ht` (dropdown d'unité, bouton
//     « Appliquer », champ quantité, prix HT) → on stamp les valeurs
//     review_* ET on marque `reviewed_base_unit_source = "human"`.
//   • Toute autre édition (ex. `product_name`, `line_total_ht` seul,
//     `content_value`, `matched_product_id`) → on ne touche PAS aux
//     champs `reviewed_*`. La ligne reste re-normalisable par l'OCR.
//
// Les lignes déjà reviewées humainement (source == "human") gardent
// leurs valeurs — on ne les écrase jamais.
// ---------------------------------------------------------------------------
export function reviewPatch(line, changes = {}) {
  const current = reviewMeasurement(line);
  const original = line.review_original || {
    quantity: line.quantity, unit: line.unit, unit_price_ht: line.unit_price_ht,
    unit_price_basis: line.unit_price_basis, line_total_ht: line.line_total_ht,
    pack_count: line.pack_count, units_per_pack: line.units_per_pack,
    total_units: line.total_units, content_value: line.content_value,
    content_unit: line.content_unit, total_weight_g: line.total_weight_g,
    total_volume_ml: line.total_volume_ml,
  };
  const isEditOnReview = (
    "reviewed_base_unit" in changes
    || "reviewed_quantity" in changes
    || "reviewed_price_ht" in changes
  );
  const priorHuman = line.reviewed_base_unit_source === "human";
  // On ne tamponne les reviewed_* que :
  //   (a) si l'édition en cours porte sur eux (choix humain explicite), OU
  //   (b) si la ligne était DÉJÀ humainement reviewée (préserve le choix).
  const shouldStampReview = isEditOnReview || priorHuman;
  const patch = { review_original: original };
  if (shouldStampReview) {
    patch.reviewed_base_unit = current.base || null;
    patch.reviewed_quantity = current.quantity;
    patch.reviewed_price_ht = ("reviewed_quantity" in changes
                               || "reviewed_base_unit" in changes
                               || "line_total_ht" in changes)
      ? null : current.price;
    // Toute édition sur les champs reviewed est un choix humain explicite.
    if (isEditOnReview) patch.reviewed_base_unit_source = "human";
  }
  if (positive(changes.line_total_ht) && line.status_tier === "zero_price") {
    patch.status_tier = "probable";
  }
  return { ...patch, ...changes };
}
